import storage from 'store'
import { getActiveCloudServerKey, normalizeCloudServerKey } from '@/utils/cloudServer'

const ONE_DAY = 24 * 60 * 60 * 1000
const CLOUD_VIEW_MODE_KEY = 'ViwoodsCloudViewMode'
const CLOUD_SORT_STATE_KEY = 'ViwoodsCloudSortState'
const CLOUD_HOME_STATE_KEY = 'ViwoodsCloudHomeState'
const CLOUD_APP_STATE_PREFIX = 'ViwoodsCloudAppState:'
const CLOUD_SORT_FIELDS = ['createTime', 'modifyTime', 'name']

export function normalizeCloudViewMode(mode) {
  return mode == 'grid' ? 'grid' : 'list'
}

export function normalizeCloudSortField(field) {
  return CLOUD_SORT_FIELDS.includes(field) ? field : 'createTime'
}

function now() {
  return Date.now()
}

function getSessionStorage() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null
  }
  try {
    return window.sessionStorage
  } catch (error) {
    return null
  }
}

function readSessionFreshState(key) {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) {
    return null
  }
  try {
    const rawValue = sessionStorage.getItem(key)
    if (!rawValue) {
      return null
    }
    const value = JSON.parse(rawValue)
    if (!value || typeof value != 'object') {
      sessionStorage.removeItem(key)
      return null
    }
    const updatedAt = Number(value.updatedAt)
    if (!Number.isFinite(updatedAt) || now() - updatedAt > ONE_DAY) {
      sessionStorage.removeItem(key)
      return null
    }
    return value
  } catch (error) {
    try {
      sessionStorage.removeItem(key)
    } catch (removeError) {
      return null
    }
    return null
  }
}

function writeSessionState(key, value) {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) {
    return null
  }
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
    return value
  } catch (error) {
    return null
  }
}

function removeSessionState(key) {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) {
    return
  }
  try {
    sessionStorage.removeItem(key)
  } catch (error) {
    // Ignore session storage cleanup failures.
  }
}

function getStateServerKey(serverKey) {
  return normalizeCloudServerKey(serverKey) || getActiveCloudServerKey()
}

function getCloudHomeStateKey(serverKey) {
  return `${CLOUD_HOME_STATE_KEY}:${getStateServerKey(serverKey)}`
}

function getCloudAppStateKey(appKey, serverKey) {
  return `${CLOUD_APP_STATE_PREFIX}${getStateServerKey(serverKey)}:${appKey}`
}

function isScopedCloudAppStateKey(key, serverKey) {
  const prefix = serverKey
    ? `${CLOUD_APP_STATE_PREFIX}${getStateServerKey(serverKey)}:`
    : CLOUD_APP_STATE_PREFIX
  return key && key.indexOf(prefix) == 0
}

function isLegacyCloudAppStateKey(key) {
  if (!key || key.indexOf(CLOUD_APP_STATE_PREFIX) != 0) {
    return false
  }
  const suffix = key.slice(CLOUD_APP_STATE_PREFIX.length)
  return suffix.indexOf(':') < 0
}

export function getCloudViewMode(fallback = 'list') {
  return normalizeCloudViewMode(storage.get(CLOUD_VIEW_MODE_KEY) || fallback)
}

export function setCloudViewMode(mode, forceNotify = false) {
  const viewMode = normalizeCloudViewMode(mode)
  const previousRaw = storage.get(CLOUD_VIEW_MODE_KEY)
  const previous = previousRaw ? normalizeCloudViewMode(previousRaw) : ''
  storage.set(CLOUD_VIEW_MODE_KEY, viewMode)
  if ((previous != viewMode || forceNotify) && typeof window !== 'undefined' && typeof window.dispatchEvent == 'function') {
    window.dispatchEvent(new CustomEvent('viwoods-cloud-view-mode-change', { detail: { viewMode } }))
  }
  return viewMode
}

export function getCloudSortState(fallback = {}) {
  const value = storage.get(CLOUD_SORT_STATE_KEY)
  const fallbackField = normalizeCloudSortField(fallback.sortField)
  const fallbackDesc = typeof fallback.sortDesc == 'boolean' ? fallback.sortDesc : true
  if (!value || typeof value != 'object') {
    return {
      sortField: fallbackField,
      sortDesc: fallbackDesc
    }
  }
  return {
    sortField: normalizeCloudSortField(value.sortField || fallbackField),
    sortDesc: typeof value.sortDesc == 'boolean' ? value.sortDesc : fallbackDesc
  }
}

export function setCloudSortState(state = {}) {
  const previous = getCloudSortState()
  const sortField = normalizeCloudSortField(state.sortField || previous.sortField)
  const sortDesc = typeof state.sortDesc == 'boolean' ? state.sortDesc : previous.sortDesc
  const nextState = { sortField, sortDesc }
  storage.set(CLOUD_SORT_STATE_KEY, nextState)
  if (
    (previous.sortField != sortField || previous.sortDesc != sortDesc) &&
    typeof window !== 'undefined' &&
    typeof window.dispatchEvent == 'function'
  ) {
    window.dispatchEvent(new CustomEvent('viwoods-cloud-sort-change', { detail: nextState }))
  }
  return nextState
}

export function getCloudHomeState() {
  return readSessionFreshState(getCloudHomeStateKey())
}

export function setCloudHomeState(state) {
  if (!state || typeof state != 'object') {
    return null
  }
  const nextState = Object.assign({}, state, { updatedAt: now() })
  return writeSessionState(getCloudHomeStateKey(), nextState)
}

export function clearCloudHomeState(serverKey = '') {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) {
    return
  }
  const keys = [CLOUD_HOME_STATE_KEY]
  if (serverKey) {
    keys.push(getCloudHomeStateKey(serverKey))
  } else {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.indexOf(`${CLOUD_HOME_STATE_KEY}:`) == 0) {
        keys.push(key)
      }
    }
  }
  keys.forEach(removeSessionState)
}

export function getCloudAppState(appKey) {
  if (!appKey) {
    return null
  }
  return readSessionFreshState(getCloudAppStateKey(appKey))
}

export function setCloudAppState(appKey, state) {
  if (!appKey || !state || typeof state != 'object') {
    return null
  }
  const nextState = Object.assign({}, state, { updatedAt: now() })
  return writeSessionState(getCloudAppStateKey(appKey), nextState)
}

export function clearCloudAppState(appKey, serverKey = '') {
  if (!appKey) {
    return
  }
  removeSessionState(getCloudAppStateKey(appKey, serverKey))
  removeSessionState(`${CLOUD_APP_STATE_PREFIX}${appKey}`)
}

export function clearCloudAppStates(serverKey = '') {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) {
    return
  }
  const keys = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (serverKey ? isScopedCloudAppStateKey(key, serverKey) : isScopedCloudAppStateKey(key) || isLegacyCloudAppStateKey(key)) {
      keys.push(key)
    }
  }
  keys.forEach(removeSessionState)
}
